/**
 * Cookie consent management hook (LFPDPPP compliant).
 * Stores user choice in localStorage for 12 months.
 */
import { useState, useEffect, useCallback } from 'react';
import { updateAnalyticsConsent } from '../services/analytics';

const STORAGE_KEY = 'quitamelo_cookie_consent';
const CONSENT_VERSION = 1; // bump if banner copy/categories change
const TWELVE_MONTHS_MS = 365 * 24 * 60 * 60 * 1000;

const readStoredConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    if (Date.now() - parsed.timestamp > TWELVE_MONTHS_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeStoredConsent = (analyticsGranted) => {
  const payload = {
    version: CONSENT_VERSION,
    analytics: analyticsGranted,
    timestamp: Date.now()
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  return payload;
};

export const useCookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [consent, setConsent] = useState(null);

  // On mount: load existing consent and apply it
  useEffect(() => {
    const stored = readStoredConsent();
    if (stored) {
      setConsent(stored);
      updateAnalyticsConsent(stored.analytics);
    } else {
      setShowBanner(true);
    }
  }, []);

  const acceptAll = useCallback(() => {
    const payload = writeStoredConsent(true);
    setConsent(payload);
    updateAnalyticsConsent(true);
    setShowBanner(false);
  }, []);

  const rejectAll = useCallback(() => {
    const payload = writeStoredConsent(false);
    setConsent(payload);
    updateAnalyticsConsent(false);
    setShowBanner(false);
  }, []);

  return { showBanner, consent, acceptAll, rejectAll };
};
